import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { observer } from "mobx-react-lite";
import { DesignEditor } from "src/components/DesignEditor";
import { Editor } from "src/types/Editor";
import { useApi } from "src/contexts/api.context";
import { useWorkspaceQuery } from "src/hooks/useWorkspaceQuery";
import { hydrateWorldFromFixture, type CostFixture } from "src/dev/CostFixture";
import { ArchitecturalMetrics } from "src/types/ArchitecturalMetrics";

const fixtureModules = import.meta.glob<CostFixture>("../dev/cost-fixtures/*.json", {
  eager: true,
  import: "default",
});

const fixtures: Record<string, CostFixture> = Object.fromEntries(
  Object.entries(fixtureModules).map(([path, mod]) => {
    const id = path.split("/").pop()?.replace(/\.json$/, "") ?? "";
    return [id, mod];
  })
);

export const CostEditorScreen = observer(function CostEditorScreen() {
  const { fixtureId = "" } = useParams<{ fixtureId: string }>();
  const api = useApi();
  const workspace = useWorkspaceQuery();
  const fixture = fixtures[fixtureId];

  const editor = useMemo(() => {
    // Empty projectId/scenarioId — useScenarioQuery is gated on projectId so
    // it won't fire, and AutoSave's reaction only runs on subsequent config
    // changes (prev must exist), so the initial loadFromWorld is silent.
    const e = new Editor(api, "", "");
    return e;
  }, [api]);

  useEffect(() => {
    return () => editor.destory();
  }, [editor]);

  // Test-only driver. Exposes a stable, JSON-serialisable surface to the
  // Playwright cost-data spec so it can read the design-generator's output
  // and programmatically swap the canvas selection without touching MobX
  // observables across page.evaluate boundaries.
  useEffect(() => {
    const driver = {
      getMeta: () => ({
        area: fixture?.area ?? null,
        lng: editor.world?.longitude ?? null,
        lat: editor.world?.latitude ?? null,
        buildingCount: editor.world?.buildings.length ?? 0,
        buildings: (editor.world?.buildings ?? []).map((b) => {
          const m = new ArchitecturalMetrics(b);
          const mix: Record<string, number> = {};
          for (const item of m.UnitCategoryMixPerCategory) mix[item.name] = item.count;
          return {
            label: b.label,
            floorCount: b.floorCount,
            units: m.ApartmentQuantityTotal,
            mix,
          };
        }),
      }),
      getOptionsCount: () => editor.designGenerator.list.length,
      getOption: (i: number) => {
        const item = editor.designGenerator.list[i];
        if (!item) return null;
        return {
          label: `Option ${i + 1}`,
          totals: {
            construction: item.costMetrics.summary.construction,
            units: item.architecturalMetrics.ApartmentQuantityTotal,
            gia: item.architecturalMetrics.GIATotal,
          },
          perBuilding: item.costMetricsPerBuilding.map((s, idx) => ({
            index: idx,
            construction: s.construction,
          })),
        };
      },
      selectBuilding: (i: number) => {
        const b = editor.world?.buildings[i];
        editor.view.selection.setBuildings(b ? [b.id] : []);
      },
      // Identifies which cost engine produced these numbers. Today we only
      // ship one (calculateCost in apps/app/src/workers/cost-engine.ts), so
      // this is a constant. When alternates exist (per-region simpler engine,
      // DE-specific engine) the constant becomes a real selector and existing
      // snapshots remain interpretable via this field.
      getEngineId: () => "default",
      // Hash of /public/cost_data/*.json so a snapshot diff caused by data
      // updates is distinguishable from one caused by code/formula changes.
      // Concatenates files in sorted-name order, SHA-256, first 12 hex.
      getCostDataHash: async () => {
        const files = [
          "kit_base_build_element_reference.json",
          "kit_module.json",
          "kit_module_bill_of_material.json",
          "kit_module_element_reference.json",
          "kit_unit.json",
          "kit_unit_module.json",
        ];
        const bodies = await Promise.all(
          files.map(async (f) => {
            const r = await fetch(`/cost_data/${f}`);
            return await r.text();
          })
        );
        const buf = new TextEncoder().encode(bodies.join("\n"));
        const digest = await crypto.subtle.digest("SHA-256", buf);
        return Array.from(new Uint8Array(digest))
          .map((b) => b.toString(16).padStart(2, "0"))
          .join("")
          .slice(0, 12);
      },
    };
    (window as unknown as { __costEditor?: typeof driver }).__costEditor = driver;
    return () => {
      delete (window as unknown as { __costEditor?: typeof driver }).__costEditor;
    };
  }, [editor, fixture]);

  const [loaded, setLoaded] = useState(false);
  useEffect(() => {
    if (!fixture) return;
    setLoaded(false);
    const world = hydrateWorldFromFixture(fixture);
    editor.loadFromWorld(world).then(() => {
      // DesignEditor opens the bottom Explore Panel automatically — but only
      // when a real projectId scenario loads. We pass an empty projectId, so
      // toggle it ourselves so the design options table actually renders.
      if (editor.world?.hasZone) editor.view.explorePanel.toggleVisible(true);
      setLoaded(true);
    });
  }, [editor, fixture]);

  if (!fixture) {
    return (
      <div className="p-4 text-sm" data-testid="cost-fixture-missing" data-fixture-id={fixtureId}>
        <p>
          Unknown fixture: <code>{fixtureId}</code>
        </p>
        <p className="mt-2">Available:</p>
        <ul className="list-disc pl-6">
          {Object.keys(fixtures).map((id) => (
            <li key={id}>{id}</li>
          ))}
        </ul>
      </div>
    );
  }

  if (!workspace.data || !loaded) return null;

  return (
    <div
      className="overflow-hidden w-screen h-screen flex flex-row bg-elevation-3 text-gray-200 text-sm"
      data-testid="cost-editor-ready"
      data-fixture-id={fixtureId}
      data-building-count={editor.world?.buildings.length ?? 0}
    >
      <div className="flex flex-1 overflow-hidden">
        <DesignEditor editor={editor} workspace={workspace.data} />
      </div>
    </div>
  );
});
